// Reading an order's live status back — the order-status read model
// (ADR 0012) via the BFF's `GET /api/orders/{id}` (#248, ADR 0055), which
// proxies the load-generator's `GET /orders/{id}` server-side. Same-origin,
// cookie auth. Starts no trace; just reads the projection.

export const ORDER_STAGES = ["payment", "inventory", "shipping", "fraud"] as const;
export type OrderStageName = (typeof ORDER_STAGES)[number];

export interface OrderStage {
  result?: "processed" | "failed";
  reason?: string;
  at?: string;
}

export interface OrderStatus {
  orderId: string;
  status: "in_progress" | "fulfilled" | "failed";
  failedStage?: OrderStageName;
  stages: Partial<Record<OrderStageName, OrderStage>>;
  placedAt?: string;
  updatedAt: string;
}

export type OrderStatusResponse =
  | { kind: "status"; body: OrderStatus }
  // The projection hasn't caught up with the order yet — normal for the
  // first few polls right after checkout.
  | { kind: "not_found" }
  // Read model disabled (503), no session (401), or transport failed.
  | { kind: "unavailable"; status: number; error: string };

export async function fetchOrderStatus(orderId: string): Promise<OrderStatusResponse> {
  let res: Response;
  try {
    res = await fetch(`/api/orders/${encodeURIComponent(orderId)}`, {
      credentials: "include",
    });
  } catch (err) {
    return {
      kind: "unavailable",
      status: 0,
      error: err instanceof Error ? err.message : "request failed (network)",
    };
  }

  if (res.status === 200) {
    return { kind: "status", body: (await res.json()) as OrderStatus };
  }
  if (res.status === 404) {
    return { kind: "not_found" };
  }

  let error = `${res.status} ${res.statusText}`.trim();
  try {
    const b = (await res.json()) as { error?: string };
    if (b?.error) error = b.error;
  } catch {
    /* keep the status line */
  }
  return { kind: "unavailable", status: res.status, error };
}
